import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _cloneDeep from "lodash/cloneDeep";
import _map from "lodash/map";
import _round from "lodash/round";
import _reject from "lodash/reject";
import _findIndex from "lodash/findIndex";
import "core-js/modules/es6.array.slice.js";
import "core-js/modules/es6.function.name.js";
import natsort from 'natsort';
import { v1 as uuidv1 } from 'uuid';
import { changeBillCategory, clearSuggestedBills, createBills, fetchMyBills, fetchSuggestedBills, resetWorkingSuggestedBills, stageBill, unstageBill, updateBill, deleteBill, setBillPaidDate } from "../actions";
import { BILL_FACTS } from "../constants";
import { isUnauthenticateAction } from "../utils/auth";
import { findBillById } from "../utils/my-bills";

var replaceBill = function replaceBill(billToReplace, bills) {
  var id = billToReplace.id;

  if (!id) {
    return bills;
  }

  var index = _findIndex(bills, {
    id: id
  }); // preserve the order of the bills


  var billsBeforeUpdatedBill = index >= 0 ? bills.slice(0, index) : bills;
  var billsAfterUpdatedBill = index >= 0 ? bills.slice(index + 1) : [];
  return [].concat(_toConsumableArray(billsBeforeUpdatedBill), [billToReplace], _toConsumableArray(billsAfterUpdatedBill));
};

var removeBill = function removeBill(billToRemove, bills) {
  return _reject(bills, function (bill) {
    return bill.id === billToRemove.id;
  });
};

var sortNaturally = natsort({
  insensitive: true
});

var changeCategory = function changeCategory(bill, category, subcategory) {
  return _objectSpread(_objectSpread({}, bill), {}, {
    category: category,
    subcategory: subcategory
  });
};
/**
 * Converts bill.amount from cents to a dollar and cents amount
 * @param {Object} bill A bill object
 */


var changeBillAmountToDollars = function changeBillAmountToDollars(bill) {
  var amount = bill.amount;

  if (amount) {
    return _objectSpread(_objectSpread({}, bill), {}, {
      amount: _round(amount / 100, 2)
    });
  }

  return bill;
};

var reducer = function reducer() {
  var state = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var action = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var type = action.type,
      payload = action.payload,
      meta = action.meta;

  if (isUnauthenticateAction(action)) {
    return {};
  }

  switch (type) {
    case fetchMyBills.SUCCESS_TYPE:
      {
        // we keep the amount in dollars in Redux even though backend uses cents
        var bills = payload.facts[BILL_FACTS.MY_BILLS];
        return _objectSpread(_objectSpread({}, state), {}, {
          myBills: _map(bills, changeBillAmountToDollars)
        });
      }

    case createBills.SUCCESS_TYPE:
      {
        var myBills = state.myBills; // we keep the amount in dollars in Redux even though backend uses cents

        var newBillsInDollars = _map(payload, changeBillAmountToDollars);

        var _bills = [].concat(_toConsumableArray(myBills || []), _toConsumableArray(newBillsInDollars));

        return _objectSpread(_objectSpread({}, state), {}, {
          myBills: _bills.sort(function (a, b) {
            return sortNaturally(a.name, b.name);
          })
        });
      }

    case fetchSuggestedBills.SUCCESS_TYPE:
      {
        var suggestedBills = payload.facts[BILL_FACTS.SUGGESTED_BILLS];

        var billsInDollars = _map(suggestedBills, changeBillAmountToDollars);

        return _objectSpread(_objectSpread({}, state), {}, {
          fetchedSuggestedBills: billsInDollars,
          workingSuggestedBills: _cloneDeep(billsInDollars)
        });
      }

    case clearSuggestedBills.getType():
      {
        return _objectSpread(_objectSpread({}, state), {}, {
          fetchedSuggestedBills: null,
          workingSuggestedBills: null
        });
      }

    case resetWorkingSuggestedBills.getType():
      {
        return _objectSpread(_objectSpread({}, state), {}, {
          workingSuggestedBills: state.fetchedSuggestedBills
        });
      }

    case stageBill.getType():
      {
        var bill = payload.bill;
        var workingSuggestedBills = state.workingSuggestedBills; // A bill is a custom bill if it is created from scratch (rather than suggested)

        var isUserGenerated = bill.isUserGenerated || !bill.id;

        var stagedBill = _objectSpread(_objectSpread({}, bill), {}, {
          // generate a random id if the bill doesn't have one, so it can be used to unstage later if needed
          id: bill.id || uuidv1(),
          isStaged: true,
          isUserGenerated: isUserGenerated
        });

        var updatedSuggestedBills = replaceBill(stagedBill, workingSuggestedBills);
        return _objectSpread(_objectSpread({}, state), {}, {
          workingSuggestedBills: updatedSuggestedBills
        });
      }

    case unstageBill.getType():
      {
        var id = payload.id;
        var _workingSuggestedBills = state.workingSuggestedBills;

        var billToUpdateIndex = _findIndex(_workingSuggestedBills, {
          id: id
        });

        var targetBill = _workingSuggestedBills[billToUpdateIndex]; // remove bill if it is user generated

        var shouldRemoveBill = targetBill.isUserGenerated;

        var unstagedBill = _objectSpread(_objectSpread({}, targetBill), {}, {
          isStaged: false
        });

        var _updatedSuggestedBills = shouldRemoveBill ? removeBill(targetBill, _workingSuggestedBills) : replaceBill(unstagedBill, _workingSuggestedBills);

        return _objectSpread(_objectSpread({}, state), {}, {
          workingSuggestedBills: _updatedSuggestedBills
        });
      }

    case updateBill.SUCCESS_TYPE:
      {
        var _myBills = state.myBills;
        var billInDollars = changeBillAmountToDollars(payload);
        var unsortedBills = replaceBill(billInDollars, _myBills);
        return _objectSpread(_objectSpread({}, state), {}, {
          myBills: unsortedBills.sort(function (a, b) {
            return sortNaturally(a.name, b.name);
          })
        });
      }

    case changeBillCategory.START_TYPE:
      {
        var billId = meta.billId,
            newCategory = meta.newCategory,
            newSubcategory = meta.newSubcategory;
        var _myBills2 = state.myBills;
        var originalBill = findBillById(_myBills2, billId);
        var recategorizedBill = changeCategory(originalBill, newCategory, newSubcategory);
        return _objectSpread(_objectSpread({}, state), {}, {
          myBills: replaceBill(recategorizedBill, _myBills2)
        });
      }

    case changeBillCategory.FAIL_TYPE:
      {
        var _billId = meta.billId,
            previousCategory = meta.previousCategory,
            previousSubcategory = meta.previousSubcategory;
        var _myBills3 = state.myBills;

        var _originalBill = findBillById(_myBills3, _billId);

        var _recategorizedBill = changeCategory(_originalBill, previousCategory, previousSubcategory);

        return _objectSpread(_objectSpread({}, state), {}, {
          myBills: replaceBill(_recategorizedBill, _myBills3)
        });
      }

    case deleteBill.SUCCESS_TYPE:
      {
        var _billId2 = meta.billId;
        var _myBills4 = state.myBills;
        return _objectSpread(_objectSpread({}, state), {}, {
          myBills: _reject(_myBills4, {
            id: _billId2
          })
        });
      }

    case setBillPaidDate.START_TYPE:
      {
        var _billId3 = meta.billId,
            newPaidDate = meta.newPaidDate;
        var _myBills5 = state.myBills;

        var updatedBill = _objectSpread(_objectSpread({}, findBillById(_myBills5, _billId3)), {}, {
          paid_date: newPaidDate
        });

        return _objectSpread(_objectSpread({}, state), {}, {
          myBills: replaceBill(updatedBill, _myBills5)
        });
      }

    case setBillPaidDate.FAIL_TYPE:
      {
        var _billId4 = meta.billId,
            previousPaidDate = meta.previousPaidDate;
        var _myBills6 = state.myBills;

        var _updatedBill = _objectSpread(_objectSpread({}, findBillById(_myBills6, _billId4)), {}, {
          paid_date: previousPaidDate
        });

        return _objectSpread(_objectSpread({}, state), {}, {
          myBills: replaceBill(_updatedBill, _myBills6)
        });
      }

    default:
      return state;
  }
};

export default reducer;